import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Send, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return { year: d.getFullYear(), month: d.getMonth() + 1 }; };
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

// Configurações → XML Contador: envio automático (dia 1º) do ZIP do mês anterior por e-mail + envio manual
export const AccountantEmailPanel = () => {
  const [c, setC] = useState({ emails: '', enabled: false, tipo: 'todos' });
  const [history, setHistory] = useState([]);
  const [sel, setSel] = useState(prevMonth());
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/fiscal/xml/accountant`, cfg);
      setC({ emails: (r.data.emails || []).join(', '), enabled: !!r.data.enabled, tipo: r.data.tipo || 'todos' });
      setHistory(r.data.history || []);
    } catch (_e) { /* sem config ainda */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy('save');
    try {
      const emails = c.emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
      const r = await axios.put(`${API}/fiscal/xml/accountant`, { emails, enabled: c.enabled, tipo: c.tipo }, cfg);
      setC({ emails: (r.data.emails || []).join(', '), enabled: !!r.data.enabled, tipo: r.data.tipo || 'todos' });
      toast.success(r.data.enabled ? 'Envio automático ATIVADO — todo dia 1º às 05:00' : 'Configuração salva (envio automático desligado)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Falha ao salvar'); } finally { setBusy(''); }
  };
  const sendNow = async () => {
    if (!window.confirm(`Enviar agora o ZIP de ${MESES[sel.month - 1]}/${sel.year} para: ${c.emails || '(nenhum e-mail)'}?`)) return;
    setBusy('send');
    try {
      const r = await axios.post(`${API}/fiscal/xml/accountant/send`, sel, cfg);
      toast.success(`E-mail enviado: ${r.data.ok} XML(s)${r.data.fail ? ` · ${r.data.fail} falha(s) (ERROS.txt)` : ''}${r.data.attached ? ' · ZIP anexado' : ' · só link (ZIP grande)'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Falha no envio'); load(); } finally { setBusy(''); }
  };

  const years = [new Date().getFullYear(), new Date().getFullYear() - 1];
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4" data-testid="accountant-email-panel">
      <div>
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Mail size={16} /> Envio automático ao contador</h3>
        <p className="text-xs text-slate-500 mt-1">Todo <b>dia 1º às 05:00</b> o sistema gera o ZIP com os XMLs do mês anterior desta loja e envia por e-mail ao contador (ZIP anexado + link de download válido por 60 dias). Para cada loja, configure na própria loja.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">E-mail(s) do contador <span className="text-slate-400">(separe por vírgula)</span></Label>
          <Input value={c.emails} onChange={(e) => setC({ ...c, emails: e.target.value })} placeholder="contador@escritorio.com.br, fiscal@escritorio.com.br" data-testid="accountant-emails" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Documentos</Label>
          <select value={c.tipo} onChange={(e) => setC({ ...c, tipo: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="accountant-tipo">
            <option value="todos">Todos (NFC-e + NF-e)</option>
            <option value="nfce">Só NFC-e</option>
            <option value="nfe">Só NF-e</option>
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="accountant-enabled-label">
        <input type="checkbox" checked={c.enabled} onChange={(e) => setC({ ...c, enabled: e.target.checked })} className="w-4 h-4" data-testid="accountant-enabled" />
        <span className="font-medium">Ativar envio automático todo dia 1º</span>
      </label>
      <div className="flex flex-wrap items-end gap-2">
        <Button type="button" onClick={save} disabled={!!busy} className="bg-slate-900 hover:bg-black" data-testid="accountant-save-btn"><Save size={14} className="mr-1" /> {busy === 'save' ? 'Salvando…' : 'Salvar'}</Button>
        <div className="flex items-end gap-2 ml-auto">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase">Mês</Label>
            <select value={sel.month} onChange={(e) => setSel({ ...sel, month: Number(e.target.value) })} className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="accountant-month">
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase">Ano</Label>
            <select value={sel.year} onChange={(e) => setSel({ ...sel, year: Number(e.target.value) })} className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="accountant-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Button type="button" variant="outline" onClick={sendNow} disabled={!!busy || !c.emails} className="h-9" data-testid="accountant-send-btn"><Send size={14} className="mr-1" /> {busy === 'send' ? 'Enviando…' : 'Enviar agora'}</Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500">Salve a configuração antes de "Enviar agora". O envio manual usa os e-mails salvos.</p>
      {history.length > 0 && (
        <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="accountant-history">
          <div className="bg-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-600">Histórico de envios</div>
          <table className="w-full text-xs">
            <thead className="text-slate-500"><tr><th className="text-left px-3 py-1">Quando</th><th className="text-left">Mês</th><th className="text-left">Origem</th><th className="text-left">Para</th><th className="text-left">Resultado</th></tr></thead>
            <tbody>{history.map((h, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`accountant-history-row-${i}`}>
                <td className="px-3 py-1">{new Date(h.at).toLocaleString('pt-BR')}</td>
                <td>{h.label}</td>
                <td>{h.trigger === 'cron' ? 'Automático' : `Manual (${h.actor || '-'})`}</td>
                <td className="truncate max-w-[200px]">{(h.emails || []).join(', ')}</td>
                <td className={h.status === 'enviado' ? 'text-emerald-700 font-bold' : 'text-red-600 font-bold'}>
                  {h.status === 'enviado' ? `Enviado · ${h.ok} XML(s)${h.fail ? ` · ${h.fail} falha(s)` : ''}${h.size ? ` · ${fmtSize(h.size)}` : ''}` : `Erro: ${h.error}`}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
